import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarX, X } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { ROLES } from "@/shared/lib/rbac";
import { myBillingService, type MySubscriptionView } from "../api/myBillingService";
import { expiryNotice, localDateKey } from "../lib/expiryNotice";
import { lapseNoticeText } from "../lib/subscriptionLapse";
import { useSubscriptionLapse } from "../model/useSubscriptionLapse";
import { isPartnerBrand, useBrand } from "@/shared/brand";

/**
 * 套餐到期站内横幅(修复波 0926 第八批 Z3)。口径见 {@link expiryNotice}。
 * ★可关:关掉后**当天**不再弹。localStorage key 带当天日期(换天自然失效),读写都 try/catch;
 *   存储不可用(隐私窗口等)时退化为「本次挂载内关掉」,不报错。
 */
export const EXPIRY_DISMISS_KEY_PREFIX = "gscq.expiryBanner.dismissed.";

function readDismissed(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(key: string) {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    /* 存储不可用:只在本次挂载内关掉 */
  }
}

export function ExpiryBanner({
  subscription,
  showPlanLink = false,
  hideExpired = false,
  now,
}: {
  subscription: MySubscriptionView | null | undefined;
  showPlanLink?: boolean;
  /** 1005:首页「已到期」改由常驻的 {@link SubscriptionLapseBanner} 说(全员可见、不可关),这里只管「快到期」。 */
  hideExpired?: boolean;
  /** 测试注入;默认当前时刻。 */
  now?: Date;
}) {
  const navigate = useNavigate();
  const [closed, setClosed] = useState(false);
  const at = now ?? new Date();
  const notice = expiryNotice(subscription, at);
  if (!notice) return null;
  if (hideExpired && notice.kind === "expired") return null;
  const key = `${EXPIRY_DISMISS_KEY_PREFIX}${notice.kind}.${localDateKey(at)}`;
  if (closed || readDismissed(key)) return null;
  const expired = notice.kind === "expired";
  return (
    <div
      role="alert"
      data-testid="expiry-banner"
      data-kind={notice.kind}
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-4 py-2.5 text-sm text-foreground ${
        expired ? "border-destructive/40 bg-destructive/5" : "border-warning/40 bg-warning/10"
      }`}
    >
      <AlertTriangle
        className={`h-4 w-4 shrink-0 ${expired ? "text-destructive" : "text-warning"}`}
      />
      <span className="min-w-0 flex-1">{notice.text}</span>
      {showPlanLink && (
        <button
          type="button"
          onClick={() => navigate("/my-billing")}
          className="whitespace-nowrap text-xs font-medium text-primary hover:underline"
        >
          查看套餐
        </button>
      )}
      <button
        type="button"
        aria-label="今天不再提示"
        title="今天不再提示"
        onClick={() => {
          writeDismissed(key);
          setClosed(true);
        }}
        className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/**
 * 工作台首页用:只有租户超管读得到 /tenant/billing/me(其余角色后端 403),其它角色不查、不显示。
 * queryKey 与「我的套餐」页同一条(["my","subscription"]),两处共用缓存。
 */
export function TenantExpiryBanner() {
  const { effectiveRole } = useAuth();
  const isAdmin = effectiveRole === ROLES.TENANT_ADMIN;
  const { data } = useQuery({
    queryKey: ["my", "subscription"],
    queryFn: () => myBillingService.getSubscription(),
    enabled: isAdmin,
    retry: false,
  });
  if (!isAdmin) return null;
  return <ExpiryBanner subscription={data} showPlanLink hideExpired />;
}

/**
 * 1005:套餐已到期(或停用)时首页顶部的**常驻**提示条 —— 全员可见、不可关。
 * 此前只有租户超管能看到一条可关的「已到期」,员工打开首页只见内容生产整块消失、毫无解释。
 * 联系人跟「我的套餐」同口径:直营「客户经理」/ 渠道「服务商」。
 */
export function SubscriptionLapseBanner() {
  const navigate = useNavigate();
  const lapse = useSubscriptionLapse();
  const partnerTenant = isPartnerBrand(useBrand());
  if (!lapse.expired) return null;
  return (
    <div
      role="alert"
      data-testid="subscription-lapse-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-2.5 text-sm text-foreground"
    >
      <CalendarX className="h-4 w-4 shrink-0 text-destructive" />
      <span className="min-w-0 flex-1">{lapseNoticeText(lapse, partnerTenant)}</span>
      <button
        type="button"
        onClick={() => navigate("/works")}
        className="whitespace-nowrap text-xs font-medium text-primary hover:underline"
      >
        去我的作品
      </button>
    </div>
  );
}
